
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, RefreshCw, Package, Edit, Eye } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';

const updateRedemptionSchema = z.object({
  status: z.enum(['pending', 'processing', 'completed', 'cancelled']),
  notes: z.string().optional(),
});

type UpdateRedemptionFormData = z.infer<typeof updateRedemptionSchema>;

interface Redemption {
  id: string;
  user_id: string;
  reward_id: string;
  points_spent: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  notes?: string;
  created_at: string;
  updated_at: string;
  rewards?: {
    name: string;
    description?: string;
    category: string;
    image_url?: string;
  };
  profiles?: {
    nickname: string;
    full_name: string;
  };
}

const RedemptionHistory = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [filteredRedemptions, setFilteredRedemptions] = useState<Redemption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRedemption, setEditingRedemption] = useState<Redemption | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const form = useForm<UpdateRedemptionFormData>({
    resolver: zodResolver(updateRedemptionSchema),
    defaultValues: {
      status: 'pending',
      notes: '',
    },
  });

  useEffect(() => {
    fetchRedemptions();
  }, []);

  useEffect(() => {
    let filtered = redemptions;

    // 狀態篩選
    if (statusFilter !== 'all') {
      filtered = filtered.filter(redemption => redemption.status === statusFilter);
    }

    // 搜尋篩選
    if (searchTerm) {
      filtered = filtered.filter(redemption =>
        redemption.rewards?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        redemption.profiles?.nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        redemption.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRedemptions(filtered);
  }, [redemptions, searchTerm, statusFilter]);

  const fetchRedemptions = async () => {
    try {
      setIsLoading(true);
      
      // First get redemptions data
      const { data: redemptionsData, error: redemptionsError } = await supabase
        .from('reward_redemptions')
        .select(`
          *,
          rewards:reward_id (
            name,
            description,
            category,
            image_url
          )
        `)
        .order('created_at', { ascending: false });

      if (redemptionsError) throw redemptionsError;

      // Get user profiles separately
      const userIds = [...new Set(redemptionsData?.map(r => r.user_id) || [])];
      
      let profilesData: any[] = [];
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, nickname, full_name')
          .in('id', userIds);

        if (profilesError) {
          console.warn('Profiles query error:', profilesError);
        } else {
          profilesData = profiles || [];
        }
      }

      // Combine the data
      const typedRedemptions: Redemption[] = (redemptionsData || []).map(item => {
        const profile = profilesData.find(p => p.id === item.user_id);
        return {
          id: item.id,
          user_id: item.user_id,
          reward_id: item.reward_id,
          points_spent: item.points_spent,
          status: item.status as 'pending' | 'processing' | 'completed' | 'cancelled',
          notes: item.notes,
          created_at: item.created_at,
          updated_at: item.updated_at,
          rewards: item.rewards ? {
            name: item.rewards.name,
            description: item.rewards.description,
            category: item.rewards.category,
            image_url: item.rewards.image_url,
          } : undefined,
          profiles: profile || { nickname: '未知用戶', full_name: '未知用戶' }
        };
      });

      setRedemptions(typedRedemptions);
    } catch (error: any) {
      console.error('Error fetching redemptions:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: error?.message || '無法載入兌換記錄',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (data: UpdateRedemptionFormData) => {
    if (!editingRedemption) return;

    try {
      const { error } = await supabase
        .from('reward_redemptions')
        .update({
          status: data.status,
          notes: data.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingRedemption.id);

      if (error) throw error;

      toast({
        title: '更新成功',
        description: '兌換記錄狀態已更新',
      });

      setIsDialogOpen(false);
      setEditingRedemption(null);
      form.reset();
      fetchRedemptions();
    } catch (error: any) {
      console.error('Error updating redemption:', error);
      toast({
        variant: 'destructive',
        title: '更新失敗',
        description: error?.message || '無法更新兌換記錄',
      });
    }
  };

  const handleEdit = (redemption: Redemption) => {
    setEditingRedemption(redemption);
    form.reset({
      status: redemption.status,
      notes: redemption.notes || '',
    });
    setIsDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '待處理';
      case 'processing':
        return '處理中';
      case 'completed':
        return '已完成';
      case 'cancelled':
        return '已取消';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-2 text-gray-600">載入兌換記錄中...</span>
      </div>
    );
  }

  // 手機版卡片布局
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col space-y-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">兌換記錄</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">管理會員積分兌換記錄</p>
          </div>
          
          <div className="flex flex-col space-y-2">
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="搜尋商品或會員..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={fetchRedemptions} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部狀態</SelectItem>
                <SelectItem value="pending">待處理</SelectItem>
                <SelectItem value="processing">處理中</SelectItem>
                <SelectItem value="completed">已完成</SelectItem>
                <SelectItem value="cancelled">已取消</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="text-sm text-gray-600">
            共 {filteredRedemptions.length} 筆兌換記錄
          </div>
        </div>

        {filteredRedemptions.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-base font-semibold text-gray-900 mb-2">暫無兌換記錄</h3>
                <p className="text-sm text-gray-600">目前還沒有任何兌換記錄</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRedemptions.map((redemption) => (
              <Card key={redemption.id} className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {redemption.rewards?.name || '未知商品'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        兌換者: {redemption.profiles?.nickname || '未知用戶'}
                      </div>
                    </div>
                    <Badge className={getStatusColor(redemption.status)}>
                      {getStatusText(redemption.status)}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">消耗積分: </span>
                      <span className="font-medium">{redemption.points_spent}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">兌換時間: </span>
                      <span>{new Date(redemption.created_at).toLocaleDateString('zh-TW')}</span>
                    </div>
                  </div>
                  
                  {redemption.notes && (
                    <div className="text-xs">
                      <span className="text-gray-500">備註: </span>
                      <span>{redemption.notes}</span>
                    </div>
                  )}
                  
                  <div className="flex space-x-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(redemption)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      編輯狀態
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>更新兌換狀態</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleUpdateStatus)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>狀態</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">待處理</SelectItem>
                          <SelectItem value="processing">處理中</SelectItem>
                          <SelectItem value="completed">已完成</SelectItem>
                          <SelectItem value="cancelled">已取消</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>備註</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex space-x-2 pt-4">
                  <Button type="submit" className="flex-1">
                    更新
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1"
                  >
                    取消
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // 桌面版表格布局
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">兌換記錄</h1>
          <p className="text-gray-600 dark:text-gray-400">管理會員積分兌換記錄</p>
        </div>
        <Button onClick={fetchRedemptions} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          重新載入
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>兌換記錄 ({filteredRedemptions.length} 筆)</CardTitle>
            <div className="flex space-x-4">
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="搜尋商品或會員..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部狀態</SelectItem>
                  <SelectItem value="pending">待處理</SelectItem>
                  <SelectItem value="processing">處理中</SelectItem>
                  <SelectItem value="completed">已完成</SelectItem>
                  <SelectItem value="cancelled">已取消</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredRedemptions.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">暫無兌換記錄</h3>
              <p className="text-gray-600">目前還沒有任何兌換記錄</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>兌換商品</TableHead>
                  <TableHead>兌換者</TableHead>
                  <TableHead>消耗積分</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>兌換時間</TableHead>
                  <TableHead>備註</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRedemptions.map((redemption) => (
                  <TableRow key={redemption.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {redemption.rewards?.name || '未知商品'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {redemption.rewards?.category}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {redemption.profiles?.nickname || '未知用戶'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {redemption.profiles?.full_name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {redemption.points_spent}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(redemption.status)}>
                        {getStatusText(redemption.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(redemption.created_at).toLocaleDateString('zh-TW')}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate text-sm">
                        {redemption.notes || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(redemption)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>更新兌換狀態</DialogTitle>
          </DialogHeader>
          {editingRedemption && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="font-medium">{editingRedemption.rewards?.name}</div>
              <div className="text-sm text-gray-500">
                兌換者: {editingRedemption.profiles?.nickname} | 
                消耗積分: {editingRedemption.points_spent}
              </div>
            </div>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleUpdateStatus)} className="space-y-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>狀態</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pending">待處理</SelectItem>
                        <SelectItem value="processing">處理中</SelectItem>
                        <SelectItem value="completed">已完成</SelectItem>
                        <SelectItem value="cancelled">已取消</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>備註</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} placeholder="輸入處理備註..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                >
                  取消
                </Button>
                <Button type="submit">
                  更新狀態
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedemptionHistory;
